'use client';

import Link from 'next/link';

import { InstrumentApi } from '@flipster/apis/v2/snapshot';
import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';

interface Props {
  product: InstrumentApi;
  quoteCurrency: string;
}

const Specification = ({ product, quoteCurrency }: Props) => {
  const rows = [
    { category: 'Base Currency', detail: product.baseCurrency },
    { category: 'Quote Currency', detail: quoteCurrency },
    {
      category: 'Commission',
      detail: (
        <>
          See the{' '}
          <Link href='/support/guide/fees'>
            <FormattedMessage id='Autogen_common.r251126.s_Fees_Reference' />
          </Link>{' '}
          for more details.
        </>
      ),
    },
    { category: 'Minimum Price Increment', detail: `${product.tickSize} ${quoteCurrency}` },
    {
      category: 'Notional Min Order Amount',
      detail: <Formatted.Number value={product.notionalMinOrderAmount} suffix={quoteCurrency} />,
    },
    {
      category: 'Notional Max Order Amount',
      detail: <Formatted.Number value={product.notionalMaxOrderAmount} suffix={quoteCurrency} />,
    },
    {
      category: 'Lot Size',
      detail: <Formatted.Number value={product.unitOrderQty} suffix={product.positionUnit} />,
    },
    {
      category: 'Max Slippage',
      detail: <Formatted.Percent value={product.defaultMaxSlippage} precision={2} toFixed isRate />,
    },
    {
      category: 'Maximum daily trading volume for buying',
      detail: <Formatted.ShortNumber value={product.maxBuyAmountDaily} precision={2} toFixed />,
    },
    {
      category: 'Maximum daily trading volume for selling',
      detail: <Formatted.ShortNumber value={product.maxSellAmountDaily} precision={2} toFixed />,
    },
  ];

  return (
    <div>
      <h2 className={css({ textStyle: 'Title/M', color: 'content.default.level-5', mt: 64, mb: 32 })}>
        <FormattedMessage id='Autogen_common.r251126.s_Pair_Specification' />
      </h2>
      <div
        className={css({
          mt: 16,
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: 'border.default.level-2',
          rounded: 8,
        })}
      >
        <table
          className={css({
            w: '100%',
            textAlign: 'left',
            borderCollapse: 'collapse',
            borderStyle: 'hidden',
            '& th': {
              textStyle: 'Text/L/Strong',
            },
            '& th, & td': {
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: 'border.default.level-2',
              p: { base: 8, tablet: 12 },
            },
            '& th:first-child, & td:first-child': {
              width: '25%',
              maxW: 260,
            },
            '& a': {
              color: 'content.accent.info',
              textDecoration: 'underline',
            },
          })}
        >
          <thead>
            <tr>
              <th>
                <FormattedMessage id='Autogen_common.r251126.s_Category' />
              </th>
              <th>
                <FormattedMessage id='Autogen_common.r251126.s_Detail' />
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ category, detail }) => (
              <tr key={category}>
                <td>{category}</td>
                <td>{detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Specification;
